import { useRef, useEffect, useCallback } from 'react'

// ── Phase colors (match PHASES palette in WorkflowMap) ───────────────────────
const PHASE_COLORS = {
    0: '#f43f5e', // rose-500  (marketing)
    1: '#3b82f6', // blue-500  (venta)
    2: '#f59e0b', // amber-500 (preliminar)
    3: '#10b981', // emerald-500 (documentos)
    4: '#8b5cf6', // violet-500 (macro revisión)
}

// ── Route colors for decision setback lines ──────────────────────────────────
const ROUTE_COLORS = {
    green: '#22c55e',
    amber: '#f59e0b',
    red:   '#ef4444',
    blue:  '#3b82f6',
}

// ── Bezier math ──────────────────────────────────────────────────────────────
function bezierPoint(t, p0, p1, p2, p3) {
    const u = 1 - t
    return {
        x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
        y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
    }
}

function bezierAngle(t, p0, p1, p2, p3) {
    const u = 1 - t
    const dx = 3 * u * u * (p1.x - p0.x) + 6 * u * t * (p2.x - p1.x) + 3 * t * t * (p3.x - p2.x)
    const dy = 3 * u * u * (p1.y - p0.y) + 6 * u * t * (p2.y - p1.y) + 3 * t * t * (p3.y - p2.y)
    return Math.atan2(dy, dx)
}

// ── Canvas helpers ───────────────────────────────────────────────────────────
function drawArrow(ctx, x, y, angle, size, color) {
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x - size * Math.cos(angle - Math.PI / 6), y - size * Math.sin(angle - Math.PI / 6))
    ctx.lineTo(x - size * Math.cos(angle + Math.PI / 6), y - size * Math.sin(angle + Math.PI / 6))
    ctx.closePath()
    ctx.fillStyle = color
    ctx.fill()
    ctx.restore()
}

function drawPill(ctx, x, y, w, h, r) {
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.lineTo(x + w - r, y)
    ctx.arcTo(x + w, y, x + w, y + r, r)
    ctx.lineTo(x + w, y + h - r)
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
    ctx.lineTo(x + r, y + h)
    ctx.arcTo(x, y + h, x, y + h - r, r)
    ctx.lineTo(x, y + r)
    ctx.arcTo(x, y, x + r, y, r)
    ctx.closePath()
}

/**
 * FlowCanvas — replaces SVG HArrow/VTurn with canvas-drawn phase-colored lines.
 *
 * Always draws:
 *   • Forward connections between adjacent nodes (solid, phase-colored)
 *
 * On decision hover:
 *   • Setback/loop routes (dashed, route-colored, with text labels)
 */
export default function FlowCanvas({ flow, hoveredDecision, containerRef, activeStep = 0, t }) {
    const canvasRef = useRef(null)

    const draw = useCallback(() => {
        const canvas = canvasRef.current
        const container = containerRef?.current
        if (!canvas || !container) return

        const ctx = canvas.getContext('2d')
        const dpr = window.devicePixelRatio || 1
        const cRect = container.getBoundingClientRect()

        // Extra width for setback arcs that extend right of the container
        const extraW = 60
        canvas.width = (cRect.width + extraW) * dpr
        canvas.height = cRect.height * dpr
        canvas.style.width = (cRect.width + extraW) + 'px'
        canvas.style.height = cRect.height + 'px'
        ctx.scale(dpr, dpr)
        ctx.clearRect(0, 0, cRect.width + extraW, cRect.height)

        // Cache node DOM rects
        const rects = {}
        flow.forEach(n => {
            const el = container.querySelector(`[data-node-id="${n.id}"]`)
            if (el) {
                const r = el.getBoundingClientRect()
                rects[n.id] = {
                    cx: r.left - cRect.left + r.width / 2,
                    cy: r.top - cRect.top + r.height / 2,
                    top: r.top - cRect.top,
                    bottom: r.bottom - cRect.top,
                    left: r.left - cRect.left,
                    right: r.right - cRect.left,
                    w: r.width,
                    h: r.height,
                }
            }
        })

        // ── 1. Forward connections (always visible) ──────────────────────────
        for (let i = 0; i < flow.length - 1; i++) {
            const src = rects[flow[i].id]
            const tgt = rects[flow[i + 1].id]
            if (!src || !tgt) continue

            const color = PHASE_COLORS[flow[i].phase] || '#9ca3af'
            const isFuture = i >= activeStep
            const alpha = isFuture ? 0.18 : 0.55

            // Same row? (use 'top' instead of 'cy' because items-start securely top-aligns them)
            const sameRow = Math.abs(src.top - tgt.top) < 20

            ctx.save()
            ctx.globalAlpha = alpha

            if (sameRow) {
                // Horizontal connection
                const goingRight = tgt.cx > src.cx
                const sx = goingRight ? src.right : src.left
                const tx = goingRight ? tgt.left : tgt.right
                
                // Average their vertical centers to ensure a perfectly 0-degree straight horizontal line
                const sharedY = (src.cy + tgt.cy) / 2
                const sy = sharedY
                const ty = sharedY

                ctx.beginPath()
                ctx.moveTo(sx, sy)
                ctx.lineTo(tx, ty)
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.setLineDash([5, 3])
                ctx.stroke()

                drawArrow(ctx, tx, ty, goingRight ? 0 : Math.PI, 7, color)
            } else {
                // Row transition — smooth S-curve
                const sx = src.cx
                const sy = src.bottom + 2
                const tx = tgt.cx
                const ty = tgt.top - 2

                const my = (sy + ty) / 2

                ctx.beginPath()
                ctx.moveTo(sx, sy)
                ctx.bezierCurveTo(sx, my, tx, my, tx, ty)
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.setLineDash([5, 3])
                ctx.stroke()

                // Arrowhead angle at endpoint
                const p0 = { x: sx, y: sy }
                const cp1 = { x: sx, y: my }
                const cp2 = { x: tx, y: my }
                const p3 = { x: tx, y: ty }
                const endAngle = bezierAngle(0.97, p0, cp1, cp2, p3)
                drawArrow(ctx, tx, ty, endAngle, 7, color)
            }

            ctx.restore()
        }

        // ── 2. Setback / decision lines (on hover only) ─────────────────────
        // TODO: re-enable once return-route criteria are defined per workflow type
        return
        if (!hoveredDecision) return // eslint-disable-line no-unreachable

        const node = flow.find(n => n.id === hoveredDecision)
        if (!node?.routes?.length) return

        const sourceRect = rects[node.id]
        if (!sourceRect) return

        node.routes.forEach((route, ri) => {
            if (!route.to) return

            const targetRect = rects[route.to]
            if (!targetRect) return

            const color = ROUTE_COLORS[route.color] || ROUTE_COLORS.amber
            const isSameRow = Math.abs(sourceRect.top - targetRect.top) < 20
            const routeCount = node.routes.length
            const offset = ri * 50 // Stagger multiple loops

            let p0, cp1, cp2, p3

            if (isSameRow) {
                const isFirstRow = sourceRect.top < 80
                if (isFirstRow) {
                    // First row: Arch BELOW the cards (above gets clipped by overflow-clip)
                    p0 = { x: sourceRect.cx, y: sourceRect.bottom + 2 }
                    p3 = { x: targetRect.cx, y: targetRect.bottom + 2 }
                    const bowY = sourceRect.bottom + 65 + offset
                    cp1 = { x: p0.x, y: bowY }
                    cp2 = { x: p3.x, y: bowY }
                } else {
                    // Other rows: Arch cleanly UP over the top of the cards
                    p0 = { x: sourceRect.cx, y: sourceRect.top - 2 }
                    p3 = { x: targetRect.cx, y: targetRect.top - 2 }
                    const bowY = sourceRect.top - 65 - offset
                    cp1 = { x: p0.x, y: bowY }
                    cp2 = { x: p3.x, y: bowY }
                }
            } else {
                // Different row: Hook around the right side of the grid
                const yStagger = routeCount > 1 ? (ri - (routeCount - 1) / 2) * 16 : 0
                p0 = { x: sourceRect.right, y: sourceRect.cy + yStagger }
                p3 = { x: targetRect.right, y: targetRect.cy + yStagger }

                // Arc far to the right, staggered
                const arcPad = 40 + offset
                const arcX = Math.max(sourceRect.right, targetRect.right, cRect.width * 0.88) + arcPad

                cp1 = { x: arcX, y: p0.y }
                cp2 = { x: arcX, y: p3.y }
            }

            // Dashed bezier curve with glow
            ctx.save()
            ctx.beginPath()
            ctx.moveTo(p0.x, p0.y)
            ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, p3.x, p3.y)
            ctx.strokeStyle = color
            ctx.lineWidth = 2.5
            ctx.setLineDash([8, 5])
            ctx.globalAlpha = 0.85
            ctx.shadowColor = color
            ctx.shadowBlur = 6
            ctx.stroke()
            ctx.restore()

            // Arrowhead at destination
            const endAngle = bezierAngle(0.98, p0, cp1, cp2, p3)
            ctx.save()
            ctx.globalAlpha = 0.95
            drawArrow(ctx, p3.x, p3.y, endAngle, 8, color)
            ctx.restore()

            // Text label at midpoint of curve
            if (route.label) {
                const labelT = routeCount > 1 ? 0.35 + ri * 0.25 : 0.5
                const mid = bezierPoint(labelT, p0, cp1, cp2, p3)
                const labelText = t ? t(route.label) : route.label

                ctx.save()
                ctx.font = 'bold 10px Inter, system-ui, -apple-system, sans-serif'
                const metrics = ctx.measureText(labelText)
                const pad = 7
                const lw = metrics.width + pad * 2
                const lh = 18
                const lx = mid.x - lw / 2
                const ly = mid.y - lh / 2

                // Dark pill background
                ctx.globalAlpha = 0.92
                ctx.fillStyle = '#111827'
                drawPill(ctx, lx, ly, lw, lh, 6)
                ctx.fill()

                // Colored border
                ctx.strokeStyle = color
                ctx.lineWidth = 1.5
                ctx.globalAlpha = 0.6
                ctx.setLineDash([])
                ctx.stroke()

                // Text
                ctx.globalAlpha = 1
                ctx.fillStyle = color
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText(labelText, mid.x, mid.y + 0.5)
                ctx.restore()
            }
        })
    }, [flow, hoveredDecision, containerRef, activeStep, t])

    useEffect(() => {
        draw()
        const observer = new ResizeObserver(draw)
        if (containerRef?.current) observer.observe(containerRef.current)
        return () => {
            observer.disconnect()
        }
    }, [draw])

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none"
            style={{ zIndex: 5 }}
        />
    )
}
